import { apiCall } from './api.js';
import { API_ENDPOINTS } from '../utils/constants.js';
import { storage } from '../utils/localStorage.js';

export const authService = {
  // Send OTP
  sendOTP: async (email, purpose) => {
    try {
      const response = await apiCall(API_ENDPOINTS.SEND_OTP, {
        method: 'POST',
        data: {
          email,
          purpose // "register" or "password_reset"
        },
        skipAutoRedirect: true // Don't auto-redirect on errors for OTP sending
      });
      
      // Based on your API docs: response.data.data.token
      if (response.data.success && response.data.data?.token) {
        storage.setSendOtpJWT(response.data.data.token);
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  
  // Verify OTP
  verifyOTP: async (otp) => {
    try {
      const sendOtpToken = storage.getSendOtpJWT();
      if (!sendOtpToken) {
        throw new Error('No OTP request token found');
      }
      
      const response = await apiCall(API_ENDPOINTS.VERIFY_OTP, {
        method: 'POST',
        data: { otp },
        authToken: sendOtpToken
      });
      
      // Based on your API docs: response.data.data.token
      if (response.data.success && response.data.data?.token) {
        storage.setVerifyOtpJWT(response.data.data.token);
        storage.setSendOtpJWT(''); // Clear send OTP token
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  
  // Register
  register: async (userData) => {
    try {
      const verifyOtpToken = storage.getVerifyOtpJWT();
      if (!verifyOtpToken) {
        throw new Error('No OTP verification token found');
      }
      
      const response = await apiCall(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        data: userData,
        authToken: verifyOtpToken,
        skipAutoRedirect: true // Don't auto-redirect during registration
      });
      
      // Based on your API docs: response has message, user but no JWT
      // After registration, user needs to login separately
      storage.clearTemporaryTokens();
      
      return response.data;
    } catch (error) {
      // Preserve the full error structure for better error handling in components
      const errorToThrow = {
        message: error.message,
        response: error.response,
        error: error.response?.data?.error || error.response?.data?.message || error.message
      };
      console.error('AuthService register error:', errorToThrow);
      throw errorToThrow;
    }
  },
  
  // Login
  login: async (email, password) => {
    try {
      const response = await apiCall(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        data: {
          email,
          password
        },
        skipAutoRedirect: true // Don't auto-redirect on 401 for login attempts
      });
      
      // Based on your API docs: response.data.token and response.data.user
      if (response.data.token) {
        storage.setJWT(response.data.token);
        storage.setUserProfile(response.data.user);
        storage.clearTemporaryTokens();
      }
      
      return response.data;
    } catch (error) {
      // Preserve the full error structure for better error handling in components
      const errorToThrow = {
        message: error.message,
        response: error.response,
        error: error.response?.data?.error || error.response?.data?.message || error.message
      };
      console.error('AuthService login error:', errorToThrow);
      throw errorToThrow;
    }
  },
  
  // Reset Password
  resetPassword: async (newPassword) => {
    try {
      const verifyOtpToken = storage.getVerifyOtpJWT();
      if (!verifyOtpToken) {
        throw new Error('No OTP verification token found');
      }
      
      const response = await apiCall(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        data: { newPassword }, // Based on your API docs
        authToken: verifyOtpToken
      });
      
      storage.clearTemporaryTokens();
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  
  // Update Password
  updatePassword: async (currentPassword, newPassword) => {
    try {
      const response = await apiCall(API_ENDPOINTS.UPDATE_PASSWORD, {
        method: 'PUT',
        data: {
          oldPassword: currentPassword, // Based on your API docs
          newPassword: newPassword      // Based on your API docs
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Update password error:', error);
      // Preserve the full error structure for better error handling
      const errorToThrow = {
        message: error.message,
        response: error.response,
        error: error.response?.data?.error || error.response?.data?.message || error.message
      };
      throw errorToThrow;
    }
  }
};